/**
 * Automation rules: list, create, update, delete, enable from template. Org-scoped.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { DagDefinition } from '@/lib/dag-types';

const QUERY_KEY = 'automation-rules';

export type ScopeType = 'all' | 'selected' | 'tagged';
export type ActionType = 'notification' | 'report' | 'proposal' | 'service_ticket' | 'webhook' | 'system_control';

export interface AutomationRule {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_enabled: boolean;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  scope_type: ScopeType;
  scope_property_ids: string[];
  scope_tags: string[];
  schedule_type: string;
  schedule_config: Record<string, unknown> | null;
  action_type: ActionType;
  action_config: Record<string, unknown>;
  cooldown_minutes: number;
  max_executions_per_day: number | null;
  legacy_rule_id: string | null;
  is_dag: boolean;
  dag_definition: DagDefinition | null;
  template_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutomationRuleInsert {
  name: string;
  description?: string;
  is_enabled?: boolean;
  trigger_type: string;
  trigger_config?: Record<string, unknown>;
  scope_type?: ScopeType;
  scope_property_ids?: string[];
  scope_tags?: string[];
  schedule_type?: string;
  schedule_config?: Record<string, unknown> | null;
  action_type: ActionType;
  action_config?: Record<string, unknown>;
  cooldown_minutes?: number;
  max_executions_per_day?: number | null;
  is_dag?: boolean;
  dag_definition?: DagDefinition | null;
  template_id?: string | null;
}

async function fetchRules(orgId: string | null): Promise<AutomationRule[]> {
  if (!orgId) return [];
  const { data, error } = await supabase
    .from('automation_rules')
    .select('*')
    .eq('organization_id', orgId)
    .order('name');
  if (error) throw error;
  return (data ?? []) as AutomationRule[];
}

export function useAutomationRules() {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [QUERY_KEY, orgId],
    queryFn: () => fetchRules(orgId),
    enabled: Boolean(orgId),
  });
  return {
    rules: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateAutomationRule() {
  const { organization } = useApp();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (row: AutomationRuleInsert) => {
      if (!organization?.id) throw new Error('No organization');
      const { data, error } = await supabase
        .from('automation_rules')
        .insert({
          organization_id: organization.id,
          name: row.name.trim(),
          description: row.description ?? null,
          is_enabled: row.is_enabled ?? true,
          trigger_type: row.trigger_type,
          trigger_config: row.trigger_config ?? {},
          scope_type: row.scope_type ?? 'all',
          scope_property_ids: row.scope_property_ids ?? [],
          scope_tags: row.scope_tags ?? [],
          schedule_type: row.schedule_type ?? 'always',
          schedule_config: row.schedule_config ?? null,
          action_type: row.action_type,
          action_config: row.action_config ?? {},
          cooldown_minutes: row.cooldown_minutes ?? 120,
          max_executions_per_day: row.max_executions_per_day ?? null,
          is_dag: row.is_dag ?? false,
          dag_definition: row.dag_definition ?? null,
          template_id: row.template_id ?? null,
          updated_at: new Date().toISOString(),
        })
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateAutomationRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<AutomationRuleInsert & { is_enabled: boolean }>;
    }) => {
      const { error } = await supabase
        .from('automation_rules')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteAutomationRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('automation_rules').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
